// Tabs interface component

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Tabs.scss';

export default function Tabs({ className, tabs }) {
  const { hash } = useLocation();

  // Keyed by hash, not by index: conditional tabs may appear once their data
  // loads and would otherwise shift the active tab under the user
  const [activeHash, setActiveHash] = useState(hash);
  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef(null);
  const tabsHeaderRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setActiveHash(hash);
  }, [hash]);

  const activeIndex = tabs.findIndex((tab) => tab.hash === activeHash);
  const active = activeIndex === -1 ? 0 : activeIndex;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) =>
        setIsSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 1 }
    );
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const header = tabsHeaderRef.current;
    if (!header) return;

    const update = () => {
      document.documentElement.style.setProperty(
        '--sticky-top-offset',
        isSticky ? `${header.offsetHeight}px` : '0px'
      );
    };

    const observer = new ResizeObserver(update);
    observer.observe(header);
    update();

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--sticky-top-offset');
    };
  }, [isSticky]);

  // Conditional tab arrays can shrink or lock the active tab under us
  const firstEnabled = tabs.findIndex((tab) => !tab.disabled);
  const safeActive =
    tabs[active] && !tabs[active].disabled ? active : Math.max(firstEnabled, 0);

  return (
    <div className={'tabs' + (className ? ' ' + className : '')}>
      <div ref={sentinelRef} className='tabs-sticky-sentinel' />
      <ul ref={tabsHeaderRef} className={'tabs-header' + (isSticky ? ' is-sticky' : '')}>
        {tabs.map((tab, i) => (
          <li
            key={i}
            className={
              'tabs-header-item tabs-header-item-' +
              tab.hash.slice(1) +
              (safeActive === i ? ' active' : '') +
              (tab.disabled ? ' disabled' : '')
            }
            onClick={() => {
              if (tab.disabled) return;
              navigate(tab.hash, { replace: true });
              setActiveHash(tab.hash);
            }}
          >
            {tab.title}
          </li>
        ))}
      </ul>
      {tabs[safeActive]?.component}
    </div>
  );
}
